import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';

const STATUS_OPTIONS = ['ACTIVE', 'EXPIRED', 'TERMINATED', 'PENDING', 'RENEWAL_DUE'];

const STATUS_COLORS = {
  ACTIVE: { bg: '#dcfce7', color: '#166534' },
  EXPIRED: { bg: '#fee2e2', color: '#991b1b' },
  TERMINATED: { bg: '#f3f4f6', color: '#6b7280' },
  PENDING: { bg: '#fef3c7', color: '#92400e' },
  RENEWAL_DUE: { bg: '#fff7ed', color: '#9a3412' },
};

const EMPTY_FORM = {
  business_associate_name: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  services_provided: '',
  execution_date: '',
  expiration_date: '',
  renewal_date: '',
  status: 'ACTIVE',
  notes: '',
};

function BAAManagement() {
  const [baas, setBaas] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const fileRef = useRef(null);
  const limit = 25;

  const loadBAAs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getBAAs({ status: filterStatus, search, limit, offset: page * limit });
      if (res.success) {
        setBaas(res.data || []);
        setTotal(res.pagination?.total || 0);
        if (res.counts) setCounts(res.counts);
      }
    } catch (err) {
      setError('Failed to load BAAs');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, search, page]);

  useEffect(() => { loadBAAs(); }, [loadBAAs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.business_associate_name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const fd = new FormData();
      Object.entries(formData).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (file) fd.append('document', file);

      if (editingId) {
        await api.updateBAA(editingId, fd);
      } else {
        await api.createBAA(fd);
      }
      resetForm();
      loadBAAs();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (baa) => {
    setEditingId(baa.id);
    setFormData({
      business_associate_name: baa.business_associate_name || '',
      contact_name: baa.contact_name || '',
      contact_email: baa.contact_email || '',
      contact_phone: baa.contact_phone || '',
      services_provided: baa.services_provided || '',
      execution_date: baa.execution_date || '',
      expiration_date: baa.expiration_date || '',
      renewal_date: baa.renewal_date || '',
      status: baa.status || 'ACTIVE',
      notes: baa.notes || '',
    });
    setFile(null);
    setShowForm(true);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete BAA for "${name}"? This will also remove the document from S3.`)) return;
    try {
      await api.deleteBAA(id);
      loadBAAs();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDownload = async (baa) => {
    setDownloading(baa.id);
    try {
      const res = await api.downloadBAADocument(baa.id);
      if (res.success && res.data.url) {
        window.open(res.data.url, '_blank');
      }
    } catch (err) {
      setError('Failed to download document');
    } finally {
      setDownloading(null);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setFile(null);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      <div style={s.headerRow}>
        <div>
          <h2 style={s.title}>Business Associate Agreements</h2>
          <p style={s.subtitle}>Track and manage BAAs for HIPAA compliance. Documents stored securely in S3.</p>
        </div>
        <button style={s.addBtn} onClick={() => { resetForm(); setShowForm(true); }}>
          + New BAA
        </button>
      </div>

      {error && <div style={s.errorMsg}>{error}</div>}

      {/* Status summary cards */}
      {counts.total > 0 && (
        <div style={s.statsRow}>
          {STATUS_OPTIONS.map((st) => (
            <div
              key={st}
              style={{
                ...s.statCard,
                border: filterStatus === st ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                cursor: 'pointer',
              }}
              onClick={() => { setFilterStatus(filterStatus === st ? '' : st); setPage(0); }}
            >
              <div style={s.statCount}>{counts[st] || 0}</div>
              <div style={s.statLabel}>{st.replace('_', ' ')}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={s.filterRow}>
        <input
          type="text"
          placeholder="Search business associates..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          style={s.searchInput}
        />
        <select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
          style={s.select}
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(st => <option key={st} value={st}>{st.replace('_', ' ')}</option>)}
        </select>
      </div>

      {/* Create / Edit form */}
      {showForm && (
        <div style={s.formCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' }}>
              {editingId ? 'Edit BAA' : 'New Business Associate Agreement'}
            </h3>
            <button style={s.cancelBtn} onClick={resetForm}>Cancel</button>
          </div>
          <form onSubmit={handleSubmit}>
            <div style={s.formGrid}>
              <div style={s.formGroup}>
                <label style={s.label}>Business Associate Name *</label>
                <input
                  type="text" required style={s.input}
                  value={formData.business_associate_name}
                  onChange={(e) => setFormData(p => ({ ...p, business_associate_name: e.target.value }))}
                  placeholder="e.g. Twilio, AWS, SendGrid"
                />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Status</label>
                <select
                  style={s.input}
                  value={formData.status}
                  onChange={(e) => setFormData(p => ({ ...p, status: e.target.value }))}
                >
                  {STATUS_OPTIONS.map(st => <option key={st} value={st}>{st.replace('_', ' ')}</option>)}
                </select>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Contact Name</label>
                <input
                  type="text" style={s.input}
                  value={formData.contact_name}
                  onChange={(e) => setFormData(p => ({ ...p, contact_name: e.target.value }))}
                />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Contact Email</label>
                <input
                  type="email" style={s.input}
                  value={formData.contact_email}
                  onChange={(e) => setFormData(p => ({ ...p, contact_email: e.target.value }))}
                />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Contact Phone</label>
                <input
                  type="tel" style={s.input}
                  value={formData.contact_phone}
                  onChange={(e) => setFormData(p => ({ ...p, contact_phone: e.target.value }))}
                />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Services Provided</label>
                <input
                  type="text" style={s.input}
                  value={formData.services_provided}
                  onChange={(e) => setFormData(p => ({ ...p, services_provided: e.target.value }))}
                  placeholder="e.g. SMS messaging, cloud hosting"
                />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Execution Date</label>
                <input
                  type="date" style={s.input}
                  value={formData.execution_date}
                  onChange={(e) => setFormData(p => ({ ...p, execution_date: e.target.value }))}
                />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Expiration Date</label>
                <input
                  type="date" style={s.input}
                  value={formData.expiration_date}
                  onChange={(e) => setFormData(p => ({ ...p, expiration_date: e.target.value }))}
                />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Renewal Date</label>
                <input
                  type="date" style={s.input}
                  value={formData.renewal_date}
                  onChange={(e) => setFormData(p => ({ ...p, renewal_date: e.target.value }))}
                />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>BAA Document (PDF, DOC, PNG, JPG)</label>
                <div style={s.fileArea}>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                    style={{ display: 'none' }}
                    onChange={(e) => setFile(e.target.files[0])}
                  />
                  <button type="button" style={s.fileBtn} onClick={() => fileRef.current?.click()}>
                    {file ? file.name : 'Choose file...'}
                  </button>
                  {file && <span style={{ fontSize: 11, color: '#6b7280' }}>({(file.size / 1024).toFixed(0)} KB)</span>}
                </div>
              </div>
            </div>
            <div style={{ marginTop: 14 }}>
              <label style={s.label}>Notes</label>
              <textarea
                style={{ ...s.input, width: '100%', minHeight: 60, resize: 'vertical', marginTop: 4 }}
                value={formData.notes}
                onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                placeholder="Internal notes about this BAA..."
              />
            </div>
            <button type="submit" style={s.saveBtn} disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Update BAA' : 'Create BAA'}
            </button>
          </form>
        </div>
      )}

      {/* BAA list */}
      <div style={s.card}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading...</div>
        ) : baas.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
            No BAAs found. Click "+ New BAA" to add your first business associate agreement.
          </div>
        ) : (
          <>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Business Associate</th>
                  <th style={s.th}>Services</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Execution</th>
                  <th style={s.th}>Expiration</th>
                  <th style={s.th}>Document</th>
                  <th style={s.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {baas.map((baa) => {
                  const sc = STATUS_COLORS[baa.status] || STATUS_COLORS.PENDING;
                  const isExpanded = expandedId === baa.id;
                  return (
                    <React.Fragment key={baa.id}>
                      <tr
                        style={{ ...s.tr, cursor: 'pointer', background: isExpanded ? '#f0f9ff' : undefined }}
                        onClick={() => setExpandedId(isExpanded ? null : baa.id)}
                      >
                        <td style={s.td}>
                          <div style={{ fontWeight: 600, color: '#111827' }}>{baa.business_associate_name}</div>
                          {baa.contact_name && <div style={{ fontSize: 11, color: '#9ca3af' }}>{baa.contact_name}</div>}
                        </td>
                        <td style={s.td}>
                          <div style={{ fontSize: 12, color: '#6b7280', maxWidth: 180 }}>{baa.services_provided || '—'}</div>
                        </td>
                        <td style={s.td}>
                          <span style={{
                            padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                            background: sc.bg, color: sc.color, whiteSpace: 'nowrap',
                          }}>
                            {baa.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td style={s.td}>
                          <span style={{ fontSize: 12, color: '#374151' }}>
                            {baa.execution_date ? new Date(baa.execution_date).toLocaleDateString() : '—'}
                          </span>
                        </td>
                        <td style={s.td}>
                          <span style={{ fontSize: 12, color: baa.status === 'RENEWAL_DUE' ? '#9a3412' : '#374151', fontWeight: baa.status === 'RENEWAL_DUE' ? 600 : 400 }}>
                            {baa.expiration_date ? new Date(baa.expiration_date).toLocaleDateString() : '—'}
                          </span>
                        </td>
                        <td style={s.td}>
                          {baa.document_s3_key ? (
                            <button
                              style={s.dlBtn}
                              disabled={downloading === baa.id}
                              onClick={(e) => { e.stopPropagation(); handleDownload(baa); }}
                            >
                              {downloading === baa.id ? '...' : (
                                <>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
                                  </svg>
                                  {' '}{baa.document_original_name?.split('.').pop()?.toUpperCase() || 'File'}
                                </>
                              )}
                            </button>
                          ) : (
                            <span style={{ fontSize: 11, color: '#9ca3af' }}>None</span>
                          )}
                        </td>
                        <td style={s.td}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button style={s.actionBtn} onClick={(e) => { e.stopPropagation(); handleEdit(baa); }}>Edit</button>
                            <button style={{ ...s.actionBtn, color: '#dc2626' }} onClick={(e) => { e.stopPropagation(); handleDelete(baa.id, baa.business_associate_name); }}>Delete</button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={7} style={{ padding: 0 }}>
                            <div style={s.detailPanel}>
                              <div style={s.detailGrid}>
                                <Detail label="Contact Name" value={baa.contact_name} />
                                <Detail label="Contact Email" value={baa.contact_email} />
                                <Detail label="Contact Phone" value={baa.contact_phone} />
                                <Detail label="Renewal Date" value={baa.renewal_date ? new Date(baa.renewal_date).toLocaleDateString() : null} />
                                <Detail label="Document" value={baa.document_original_name} />
                                <Detail label="Uploaded" value={baa.document_uploaded_at ? new Date(baa.document_uploaded_at).toLocaleString() : null} />
                                <Detail label="Created By" value={baa.created_by_first ? `${baa.created_by_first} ${baa.created_by_last || ''}`.trim() : null} />
                                <Detail label="Last Updated By" value={baa.updated_by_first ? `${baa.updated_by_first} ${baa.updated_by_last || ''}`.trim() : null} />
                              </div>
                              {baa.notes && (
                                <div style={{ marginTop: 12 }}>
                                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Notes</div>
                                  <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{baa.notes}</div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div style={s.pagination}>
                <button style={s.pageBtn} disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</button>
                <span style={{ fontSize: 13, color: '#6b7280' }}>Page {page + 1} of {totalPages} ({total} BAAs)</span>
                <button style={s.pageBtn} disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>{label}</div>
      <div style={{ fontSize: 13, color: '#374151', marginTop: 2 }}>{value}</div>
    </div>
  );
}

const s = {
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 },
  subtitle: { fontSize: 14, color: '#6b7280', margin: '4px 0 0' },
  addBtn: {
    padding: '8px 16px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600,
    background: '#2563eb', color: '#fff', cursor: 'pointer',
  },
  errorMsg: {
    background: '#fef2f2', color: '#dc2626', padding: '10px 14px',
    borderRadius: 8, marginBottom: 16, fontSize: 13,
  },
  statsRow: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' },
  statCard: {
    flex: 1, minWidth: 100, background: '#fff', borderRadius: 10,
    padding: '12px 14px', textAlign: 'center',
  },
  statCount: { fontSize: 22, fontWeight: 700, color: '#111827' },
  statLabel: { fontSize: 10, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 },
  filterRow: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' },
  searchInput: {
    flex: 1, minWidth: 200, padding: '8px 12px', borderRadius: 8,
    border: '1px solid #d1d5db', fontSize: 14, outline: 'none',
  },
  select: {
    padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
    fontSize: 13, background: '#fff', outline: 'none', minWidth: 140,
  },
  formCard: {
    background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb',
    padding: 20, marginBottom: 16,
  },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  formGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 12, fontWeight: 600, color: '#374151' },
  input: {
    padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db',
    fontSize: 13, outline: 'none',
  },
  fileArea: { display: 'flex', alignItems: 'center', gap: 8 },
  fileBtn: {
    padding: '6px 14px', borderRadius: 6, border: '1px solid #d1d5db',
    background: '#f9fafb', fontSize: 12, cursor: 'pointer', color: '#374151',
  },
  cancelBtn: {
    padding: '6px 14px', borderRadius: 6, border: '1px solid #d1d5db',
    background: '#fff', fontSize: 12, cursor: 'pointer', color: '#6b7280',
  },
  saveBtn: {
    marginTop: 16, padding: '8px 20px', borderRadius: 8, border: 'none',
    fontSize: 13, fontWeight: 600, background: '#2563eb', color: '#fff', cursor: 'pointer',
  },
  card: {
    background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600,
    color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5,
    borderBottom: '1px solid #e5e7eb', background: '#f9fafb',
  },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '10px 14px', fontSize: 13, color: '#374151', verticalAlign: 'top' },
  dlBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 8px', borderRadius: 6, border: '1px solid #d1d5db',
    background: '#fff', fontSize: 11, cursor: 'pointer', color: '#2563eb', fontWeight: 600,
  },
  actionBtn: {
    padding: '3px 8px', borderRadius: 4, border: 'none',
    background: 'transparent', fontSize: 12, cursor: 'pointer', color: '#2563eb', fontWeight: 600,
  },
  detailPanel: {
    padding: '16px 20px', background: '#f0f9ff', borderTop: '1px solid #e0f2fe',
  },
  detailGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12,
  },
  pagination: {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    gap: 16, padding: '14px 0',
  },
  pageBtn: {
    padding: '6px 14px', borderRadius: 6, border: '1px solid #d1d5db',
    background: '#fff', fontSize: 13, cursor: 'pointer',
  },
};

export default BAAManagement;
